/**
 * FASE H — Pagina Alert Sicurezza `/admin/security/alerts` (solo super_admin).
 * Lista alert antifrode con filtri + workflow acknowledge/resolve.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldAlert, RefreshCw, CheckCircle2, Eye, Loader2, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV = {
  info:     { cls: "bg-blue-100 text-blue-700",     label: "info" },
  warning:  { cls: "bg-amber-100 text-amber-800",   label: "warning" },
  critical: { cls: "bg-red-100 text-red-800",       label: "critical" },
};
const STATUS = {
  open:         { cls: "bg-red-50 text-red-800 border-red-200",       label: "Aperto" },
  acknowledged: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Preso in carico" },
  resolved:     { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Risolto" },
};


export default function AdminSecurityAlertsPage() {
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [severityFilter, setSeverityFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (severityFilter) params.set("severity", severityFilter);
      const r = await axios.get(`${API}/admin/security/alerts?${params}`,
                                    { withCredentials: true });
      setItems(r.data.items || []);
      setOpenCount(r.data.open_count || 0);
      setCriticalCount(r.data.critical_open_count || 0);
    } catch (e) {
      toast.error("Errore caricamento alert");
    } finally { setLoading(false); }
  }, [statusFilter, severityFilter]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (id, action) => {
    try {
      await axios.post(`${API}/admin/security/alerts/${id}/${action}`, {},
                          { withCredentials: true });
      toast.success(`Alert ${action}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="p-6 max-w-full" data-testid="admin-security-alerts-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <Link to="/admin/dashboard"
                 className="text-sm text-slate-500 hover:text-[#0F1F14] inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <h1 className="font-heading text-3xl text-[#0F1F14] flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-red-700" />
            Alert Sicurezza
          </h1>
          <p className="text-sm text-[#4A5E51] mt-1">
            <b className="text-red-700">{criticalCount}</b> critical open · <b>{openCount}</b> totali aperti · 7 regole antifrode attive
          </p>
        </div>
        <button onClick={load} className="p-2 hover:bg-slate-100 rounded-lg">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      <div className="bg-white border border-[#E5E2DB] rounded-2xl p-3 mb-4 flex gap-2 items-center">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                 data-testid="alerts-status-filter"
                 className="px-3 py-2 border rounded-lg text-sm">
          <option value="">Tutti gli stati</option>
          <option value="open">Aperti</option>
          <option value="acknowledged">Presi in carico</option>
          <option value="resolved">Risolti</option>
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}
                 data-testid="alerts-severity-filter"
                 className="px-3 py-2 border rounded-lg text-sm">
          <option value="">Tutte severità</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
      </div>

      <div className="space-y-2" data-testid="alerts-list">
        {items.map((a) => {
          const sev = SEV[a.severity] || SEV.info;
          const st = STATUS[a.status] || STATUS.open;
          return (
            <div key={a.id}
                  data-testid={`alert-${a.id}`}
                  className={`border rounded-lg p-3 ${st.cls} flex items-start justify-between gap-3`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${sev.cls}`}>
                    {a.event}
                  </span>
                  <span className="text-[11px] font-medium">{st.label}</span>
                  <span className="text-[11px] text-slate-500">
                    {new Date(a.triggered_at).toLocaleString("it-IT")}
                  </span>
                </div>
                <div className="text-xs text-slate-700">
                  Target: <b>{a.target_email_masked || a.target_ip || "—"}</b>
                </div>
                <pre className="text-[10px] mt-1 text-slate-600 overflow-x-auto">
                  {JSON.stringify(a.details, null, 0)}
                </pre>
              </div>
              <div className="flex flex-col gap-1">
                {a.status === "open" && (
                  <button onClick={() => doAction(a.id, "acknowledge")}
                           data-testid={`ack-${a.id}`}
                           className="text-[10px] px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700">
                    Prendi in carico
                  </button>
                )}
                {a.status !== "resolved" && (
                  <button onClick={() => doAction(a.id, "resolve")}
                           data-testid={`resolve-${a.id}`}
                           className="text-[10px] px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Risolvi
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && !loading && (
          <div className="text-center py-10 text-slate-400 italic">
            <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
            Nessun alert nel filtro corrente. Sistema tranquillo.
          </div>
        )}
      </div>
    </div>
  );
}
